import React from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { DiscreteAttributionSlider } from '../controls';
import { translateText } from '../../utils';
import css from './AdvancedSettings.css';
import {
    ATTRIBUTION_WINDOW_OPTIONS,
    ATTRIBUTION_WINDOW_OPTIONS_WITH_ZERO,
    ATTRIBUTION_WINDOW_VISIBLE_TICKS,
    ATTRIBUTION_WINDOW_VISIBLE_TICKS_WITH_ZERO,
    DEFAULT_REATTRIBUTION_WINDOW_HOURS,
    DEFAULT_SESSION_DEDUP_WINDOW_SECONDS,
    SESSION_INTERVAL_OPTIONS,
    SESSION_INTERVAL_VISIBLE_TICKS,
    formatAttributionWindowValue,
    formatSessionIntervalValue,
} from './constants';
import { sitePropType, validationErrorsPropType } from './propTypes';

const AttributionSection = ({
    site,
    readOnly,
    zeroInactivityWindowSupported,
    translate,
    validationErrors,
    onSitePatch,
}) => {
    const attributionSettingsChanged =
        (site.initial_inactivity_window ?? DEFAULT_REATTRIBUTION_WINDOW_HOURS) !==
            (site.inactivity_window_h ?? DEFAULT_REATTRIBUTION_WINDOW_HOURS) ||
        (site.initial_session_dedup_window ?? DEFAULT_SESSION_DEDUP_WINDOW_SECONDS) !==
            (site.session_dedup_window ?? DEFAULT_SESSION_DEDUP_WINDOW_SECONDS);
    const attributionWindowOptions = zeroInactivityWindowSupported
        ? ATTRIBUTION_WINDOW_OPTIONS_WITH_ZERO
        : ATTRIBUTION_WINDOW_OPTIONS;
    const attributionWindowVisibleTicks = zeroInactivityWindowSupported
        ? ATTRIBUTION_WINDOW_VISIBLE_TICKS_WITH_ZERO
        : ATTRIBUTION_WINDOW_VISIBLE_TICKS;

    return (
        <section className={css.formSection}>
            <h4>
                <Translate id="STATIC.PAGES.APPS_CONFIGURATION.ATTRIBUTION_SETTINGS" />
            </h4>
            <DiscreteAttributionSlider
                label="STATIC.PAGES.APPS_CONFIGURATION.REATTRIBUTION_WINDOW"
                tooltip="STATIC.PAGES.APPS_CONFIGURATION.REATTRIBUTION_WINDOW_TOOLTIP"
                value={site.inactivity_window_h ?? DEFAULT_REATTRIBUTION_WINDOW_HOURS}
                options={attributionWindowOptions}
                visibleTickIndices={attributionWindowVisibleTicks}
                formatValue={formatAttributionWindowValue}
                leftLabel={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.HOURS')}
                rightLabel={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.DAYS')}
                separatorIndex={24}
                disabled={readOnly}
                onChange={nextValue => onSitePatch({ inactivity_window_h: nextValue })}
            />
            <DiscreteAttributionSlider
                label="STATIC.PAGES.APPS_CONFIGURATION.MINIMUM_SESSION_INTERVAL_WINDOW"
                tooltip="STATIC.PAGES.APPS_CONFIGURATION.MINIMUM_SESSION_INTERVAL_WINDOW_TOOLTIP"
                value={site.session_dedup_window ?? DEFAULT_SESSION_DEDUP_WINDOW_SECONDS}
                options={SESSION_INTERVAL_OPTIONS}
                visibleTickIndices={SESSION_INTERVAL_VISIBLE_TICKS}
                formatValue={formatSessionIntervalValue}
                leftLabel={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.MINUTES')}
                rightLabel={translateText(translate, 'STATIC.PAGES.APPS_CONFIGURATION.HOURS')}
                separatorIndex={12}
                disabled={readOnly}
                onChange={nextValue => onSitePatch({ session_dedup_window: nextValue })}
            />
            {validationErrors.session_dedup_window && (
                <div className={css.sliderError}>{validationErrors.session_dedup_window}</div>
            )}
            {attributionSettingsChanged && (
                <div className={css.noteLine}>
                    <Translate id="STATIC.PAGES.APPS_CONFIGURATION.ATTRIBUTION_CHANGES_SAVE_IMMEDIATELY" />
                </div>
            )}
        </section>
    );
};

AttributionSection.propTypes = {
    onSitePatch: PropTypes.func.isRequired,
    readOnly: PropTypes.bool.isRequired,
    site: sitePropType.isRequired,
    translate: PropTypes.func.isRequired,
    validationErrors: validationErrorsPropType.isRequired,
    zeroInactivityWindowSupported: PropTypes.bool.isRequired,
};

export default AttributionSection;
